import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "react-bootstrap";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";

function WorkOrdersAdd({ showAddModal }) {
  const { handleSubmit } = useForm();
  const navigate = useNavigate();

  /*   All States Below
   ********************************************* */

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOrderNotesModal, setShowOrderNotesModal] = useState(false);

  const [billingAddress, setBillingAddress] = useState({});
  const [shippingAddress, setShippingAddress] = useState(null);
  const [WOData, setWOData] = useState({});

  // Customer search states
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerLimit, setCustomerLimit] = useState(10);
  const [customerTotal, setCustomerTotal] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  /*   All Functions Below
   ********************************************* */

  const addWorkOrderData = async (formData, e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const { data } = await API.post(
        "/work-orders-add",
        {
          data: {
            ...formData,
            customerName: selectedCustomer?.customerName,
            customerId: selectedCustomer?.id,
            contactData: JSON.stringify({}),
            billingAddress,
            shippingAddress,
            ...WOData,
            WOStatus: "pending",
          },
        },
        { headers: authHeader() }
      );
      if (data?.message?.type === "success") {
        let selectedWorkOrder = data?.data;
        navigate(
          `/work-order/details/pending-processing/${selectedWorkOrder?.woId}?page=1`
        );
        showAddModal(false);
        return;
      } else {
        console.log(data);
        alert("something went wrong");
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedCustomer = async (customerData) => {
    // get default shipping address from api
    const { data } = await API.post(
      "/customer/ship-address/default/" + customerData?.customerId,
      {
        isPrimary: false,
      },
      {
        headers: authHeader(),
      }
    );

    let _shippingAddress = {};
    if (data?.success) {
      _shippingAddress = data?.data || {};
    } else {
      _shippingAddress = {};
    }

    setSelectedCustomer(customerData);
    setShowOrderNotesModal(!!customerData?.orderNotes);
    setBillingAddress({
      ...billingAddress,
      description: "",
      addressCompany: customerData?.addressCompany,
      addressLine1: customerData?.addressLine1,
      addressLine2: customerData?.addressLine2,
      city: customerData?.addressCity,
      state: customerData?.addressState,
      zip: customerData?.addressZipCode,
      country: customerData?.addressCountry,
    });

    // get addressGroundTransitDays if available

    setShippingAddress({
      description: _shippingAddress.description,
      addressCompany: _shippingAddress.companyName,
      addressLine1: _shippingAddress?.addressLine1,
      addressLine2: _shippingAddress?.addressLine2,
      city: _shippingAddress?.city,
      state: _shippingAddress?.state,
      zip: _shippingAddress?.zip,
      country: _shippingAddress?.country,
      email: _shippingAddress?.email,
      phone: _shippingAddress?.phone,
      signatureRelease: _shippingAddress?.signatureRelease,
      authorizePerson: _shippingAddress?.authorizePerson,
      selfCollect: _shippingAddress?.selfCollect,
      authorizeLocation: _shippingAddress?.authorizeLocation,
      serviceId: _shippingAddress?.serviceId,
      addressGroundTransitDays:
        _shippingAddress?.addressGroundTransitDays || null,
    });

    var currentDate = new Date();
    var formattedDateTime = currentDate
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    setWOData({
      locationId: customerData?.locationId,
      taxExamption: customerData?.isTaxExamption,
      taxRateId: customerData?.taxAccountId,
      paymentTermsId: customerData?.paymentTermsId,
      salesPersonId: customerData?.salesPersonId,
      insideSalesPersonId: customerData?.salesManager,
      WODate: formattedDateTime,
    });
  };

  // Search function
  const handleCustomerSearch = async (opts = {}) => {
    try {
      setCustomerLoading(true);
      const pageToUse = opts.page || 1;
      let body = {
        page: pageToUse,
        limit: customerLimit,
        query: customerQuery,
        searchFields: ["customerName", "customerId", "accountPayableContact"],
      };
      const { data } = await API.post("/customers/search-pagination", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setCustomerResults(data?.workOrders || []);
        setCustomerTotal(data?.total || 0);
        setCustomerPage(pageToUse);
      }
      setCustomerLoading(false);
    } catch (err) {
      setCustomerLoading(false);
    }
  };

  // Load more function
  const handleCustomerLoadMore = async () => {
    try {
      setCustomerLoading(true);
      const nextPage = customerPage + 1;
      let body = {
        page: nextPage,
        limit: customerLimit,
        query: customerQuery,
        searchFields: ["customerName", "customerId", "accountPayableContact"],
      };
      const { data } = await API.post("/customers/search-pagination", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setCustomerResults((prev) => [...prev, ...(data?.workOrders || [])]);
        setCustomerTotal(data?.total || 0);
        setCustomerPage(nextPage);
      }
      setCustomerLoading(false);
    } catch (err) {
      setCustomerLoading(false);
    }
  };

  // Effect to auto-search on filter/limit change
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (customerLimit) {
        handleCustomerSearch({ page: 1 });
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [customerLimit]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (customerQuery === "") {
        setCustomerPage(1);
        setCustomerResults([]);
        setCustomerTotal(null);
        handleCustomerSearch({ page: 1, query: "" });
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [customerQuery]);

  return (
    <div className="row">
      <div className="col-12 px-3">
        {/*    ADD FORM START
         ********************************************* */}
        <div className="mb-3">
          <h3>Add Work Order</h3>
        </div>
        <form className="" onSubmit={handleSubmit(addWorkOrderData)}>
          <div>
            <div className="fw-bold fs-4 mb-2">Search Customer</div>
            <div className="mb-3">
              <label>Results per page</label>
              <select
                className="form-select"
                value={customerLimit}
                onChange={(e) => {
                  setCustomerLimit(parseInt(e.target.value));
                  setCustomerPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="d-flex align-items-center gap-1 mb-3">
              <input
                type="text"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                className="form-control"
                placeholder="Search customers"
              />
              <button
                type="button"
                onClick={() => handleCustomerSearch({ page: 1 })}
                className="btn btn-primary"
              >
                Search
              </button>
              {customerQuery !== "" && (
                <button
                  type="button"
                  onClick={() => setCustomerQuery("")}
                  className="btn btn-danger"
                >
                  Clear
                </button>
              )}
            </div>
            {customerLoading && (
              <div className="px-2 py-2 text-muted d-flex align-items-center">
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Searching...
              </div>
            )}
            {selectedCustomer && (
              <div className="my-3">
                <div className="fw-bold fs-4 mb-1 px-1">Selected Customer</div>
                <div
                  className="bg-white border-primary border py-2 shadow-lg"
                  style={{ borderRadius: 8 }}
                >
                  <div className="row ps-3 pe-1">
                    <div className="col-2">
                      C-{selectedCustomer?.customerId}
                    </div>
                    <div className="col-8">
                      {truncateString(selectedCustomer?.customerName, 40)}
                    </div>
                    <div className="col-2">
                      <div
                        onClick={() => setSelectedCustomer(null)}
                        className="btn btn-sm btn-danger p-0 px-1"
                      >
                        <i className="mdi mdi-delete"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {customerResults.length > 0 && (
              <div
                className="border rounded mb-2"
                style={{ maxHeight: "40vh", overflowY: "auto" }}
              >
                <div className="px-2 py-1 text-end text-muted small">
                  Showing {customerResults.length} of {customerTotal}
                </div>
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>C-ID</th>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerResults.map((c) => (
                      <tr key={c.customerId}>
                        <td>C-{c.customerId}</td>
                        <td>{c.customerName}</td>
                        <td>{c.accountPayableContact}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-success"
                            type="button"
                            onClick={() => handleSelectedCustomer(c)}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {customerTotal > customerResults.length && (
                  <div className="p-2 text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleCustomerLoadMore}
                      disabled={customerLoading}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            )}
            {customerResults.length === 0 && !customerLoading && (
              <p className="text-muted">No customers found</p>
            )}
          </div>
          {/*   Action Buttons
           ********************************************* */}
          <div className={`mt-4 mb-2 d-flex justify-content-end`}>
            <div>
              <button
                className="btn px-1 btn-secondary"
                type="button"
                onClick={() => {
                  showAddModal(false);
                }}
              >
                Cancel
              </button>
              <button
                disabled={!selectedCustomer || loading}
                className="btn btn-primary ms-1"
                type="submit"
              >
                Add
              </button>
            </div>
          </div>
        </form>
      </div>

      <Modal
        show={showOrderNotesModal}
        onHide={() => setShowOrderNotesModal(false)}
        centered
        contentClassName="shadow-lg border border-2 border-primary"
        backdropClassName="order-notes-backdrop"
        style={{ zIndex: 1090 }}
      >
        <style>{`
          .order-notes-backdrop {
            z-index: 1085 !important;
            background-color: rgba(0, 0, 0, 0.6) !important;
            opacity: 1 !important;
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>
            Order Notes for {selectedCustomer?.customerName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {selectedCustomer?.orderNotes}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowOrderNotesModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WorkOrdersAdd;
